import { useEffect, useState } from 'react';
import { ApiError, resolvePaints } from './api';
import type { OptionScope } from '@higgins/m2q-pricing';
import type { PaintProductFromApi } from './types';

type Resolved = { products: Record<string, PaintProductFromApi | null> } | { error: string } | null;

/** The price-book paints an option lists without a typed price — the ones that need their price on the date. */
export const productIdsToPrice = (scope: OptionScope): string[] =>
  [...new Set((scope.paints ?? []).filter((p) => p.productId && p.costPerLitre === undefined).map((p) => p.productId as string))].sort();

/**
 * Each listed product at its price on the pricing date (a newer price for the
 * same product applies from its start; null = not priced on that date). null
 * while loading; nothing to fetch for an option on the generic paints.
 */
export function useResolvedPaints(ids: string[], date: string): Resolved {
  const key = ids.join(',');
  const [state, setState] = useState<Resolved>(ids.length ? null : { products: {} });
  useEffect(() => {
    const list = key ? key.split(',') : [];
    if (!list.length) {
      setState({ products: {} });
      return;
    }
    let current = true;
    setState(null);
    resolvePaints(list, date).then(
      ({ products }) => current && setState({ products }),
      (err: unknown) => current && setState({ error: err instanceof ApiError ? err.message : 'The option’s price-book paints could not be priced — check the signal.' }),
    );
    return () => {
      current = false;
    };
  }, [key, date]);
  return state;
}
